// Copyright (c) Microsoft. All rights reserved.

import {
  Action,
  Dispatch,
  MiddlewareAPI,
  ThunkMiddleware,
  Tuple,
  UnknownAction,
  configureStore,
} from '@reduxjs/toolkit';
import { AppState } from '../../libs/models/app/AppState';
import { ConversationsState } from '../../libs/models/conversations/ConversationsState';
import { ConversationsState as ConversationsStateV2 } from '../../libs/models/conversations/ConversationsState_v2';
import { agUiMiddleware } from '../features/message-relay/agui/agUiMiddleware';
import { signalRMiddleware } from '../features/message-relay/signalRMiddleware';
import { UsersState } from '../../libs/models/users/UsersState';
import { AIModelsState } from '../../libs/models/app/AIModel';
import resetStateReducer, { resetApp } from './rootReducer';
import { PluginsState } from '../../libs/models/app/Plugin';
import { CategoriesState } from '../features/studio/categorySlice';
import { PluginStudioState } from '../features/studio/pluginStudioSlice';
import { ToolState } from '../../libs/models/app/mcp/Tool';

export type StoreMiddlewareAPI = MiddlewareAPI<Dispatch, RootState>;
export type Store = typeof store;
export const store = configureStore<RootState, Action, Tuple<Array<ThunkMiddleware<RootState, UnknownAction>>>>({
  reducer: resetStateReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false })
      .concat(agUiMiddleware) // 채팅 메시지 (AG-UI SSE)
      .concat(signalRMiddleware), // 실시간 이벤트
});

export interface RootState {
  app: AppState;
  conversations: ConversationsState;
  conversations_v2: ConversationsStateV2;
  users: UsersState;
  models: AIModelsState;
  plugins: PluginsState;
  category: CategoriesState;
  pluginStudio: PluginStudioState;
  tools: ToolState;
}

export const getSelectedChatID = (): string => {
  return store.getState().conversations.selectedId;
};

export const getSelectedChatID_V2 = (): string => {
  return store.getState().conversations_v2.selectedId;
};

export type AppDispatch = typeof store.dispatch;

// Function to reset the app state
export const resetState = () => {
  store.dispatch(resetApp());
};
